import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { useSiteSettings } from '@/hooks/useSiteData';
import paypalBadge from '@/assets/paypal-badge.png';
import logoKarmaio from '@/assets/logo.png';

export function Footer() {
  const { data: settings } = useSiteSettings();

  return (
    <footer className="font-epilogue relative z-10 bg-card/95 backdrop-blur-sm border-t border-border">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Contact */}
          <div className="space-y-4 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3">
              <img src={logoKarmaio} alt="Karmaio" width={150} height={170} className="h-16 w-auto shadow-lg" loading="lazy" decoding="async" />
              <h3 className="font-urbanist text-xl font-bold text-gradient-coral">
                {settings?.site_name || 'Karmaio'}
              </h3>
            </div>
            
            <div className="flex flex-col items-center md:items-start gap-3 mt-4">
              {settings?.address && (
                <div className="flex items-start gap-2 text-sm text-foreground/80">
                  <MapPin className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                  <span>{settings.address}</span>
                </div>
              )}
              {settings?.phone && (
                <a 
                  href={`tel:${settings.phone}`}
                  className="flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
                >
                  <Phone className="h-4 w-4 text-primary" />
                  <span>{settings.phone}</span>
                </a>
              )}
              {settings?.email && (
                <a 
                  href={`mailto:${settings.email}`}
                  className="flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
                >
                  <Mail className="h-4 w-4 text-primary" />
                  <span>{settings.email}</span>
                </a>
              )}
            </div>

            {/* Social icons */}
            {(settings?.facebook_url || settings?.instagram_url) && (
              <div className="flex items-center justify-center md:justify-start gap-3 mt-4">
                {settings?.facebook_url && (
                  <a
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:scale-110 hover:shadow-[0_0_20px_hsl(8_85%_55%/0.5)]"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
                {settings?.instagram_url && (
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:scale-110 hover:shadow-[0_0_20px_hsl(8_85%_55%/0.5)]"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="text-center md:text-left">
            <h4 className="font-urbanist font-semibold text-foreground mb-4">{settings?.site_name || 'Karmaio'}</h4>
            <div className="flex flex-col items-center md:items-start gap-2">
              <Link to="/massaggi" className="text-sm text-foreground/70 hover:text-primary transition-colors underline-slide">
                Massaggi
              </Link>
              <Link to="/tantra" className="text-sm text-foreground/70 hover:text-primary transition-colors underline-slide">
                Tantra
              </Link>
              <Link to="/corsi" className="text-sm text-foreground/70 hover:text-primary transition-colors underline-slide">
                Corsi
              </Link>
              <Link to="/eventi" className="text-sm text-foreground/70 hover:text-primary transition-colors underline-slide">
                Eventi
              </Link>
              <Link to="/gift-card" className="text-sm text-foreground/70 hover:text-primary transition-colors underline-slide">
                Gift Card
              </Link>
            </div>
          </div>

          {/* Payments - BIGGER PayPal icon */}
          <div className="text-center md:text-left">
            <h4 className="font-urbanist font-semibold text-foreground mb-4">Pagamenti Sicuri</h4>
            <div className="bg-background/50 rounded-xl p-4 border border-border/50 inline-block">
              <img 
                src={paypalBadge} 
                alt="PayPal - Pagamenti Sicuri" 
                width={240}
                height={96}
                loading="lazy"
                decoding="async"
                className="h-20 md:h-24 w-auto object-contain drop-shadow-md"
              />
            </div>
            <p className="text-xs text-foreground/60 mt-3">
              Pagamenti protetti e sicuri tramite PayPal
            </p>
          </div>

          {/* Map - lightweight link (full embed only on /contatti) */}
          <div className="text-center md:text-left">
            <h4 className="font-urbanist font-semibold text-foreground mb-4">Dove Siamo</h4>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Via+Varazze+4,+20149+Milano"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Apri Via Varazze 4, Milano su Google Maps"
              className="block rounded-xl overflow-hidden border border-border/50 h-36 shadow-lg mx-auto md:mx-0 max-w-xs md:max-w-none bg-muted hover:border-primary/50 transition-colors relative group"
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <span className="text-foreground font-semibold">Via Varazze 4, Milano</span>
                <span className="text-xs text-muted-foreground mt-1">Apri in Google Maps ↗</span>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Copyright & Legal */}
      <div className="bg-background/80 border-t border-border/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-foreground/60 text-center md:text-left">
              © {new Date().getFullYear()} • {settings?.site_name || 'Karmaio'} Massaggi - Studio Massaggi | P.IVA 03950940126 | All Rights Reserved
            </p>
            <div className="flex items-center gap-4 text-xs flex-wrap justify-center">
              <Link to="/privacy-policy" className="text-foreground/60 hover:text-primary transition-colors">
                Cookie & Privacy Policy
              </Link>
              <span className="text-foreground/40">|</span>
              <Link to="/termini-condizioni" className="text-foreground/60 hover:text-primary transition-colors">
                Termini e condizioni
              </Link>
              <span className="text-foreground/40">|</span>
              <Link to="/recesso" className="text-foreground/60 hover:text-primary transition-colors">
                Diritto di recesso
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
